import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, KeyRound, Globe, Shield, Lock,
    Clock, Hash, Loader2, Search, ChevronLeft,
    ChevronRight, ChevronsLeft, ChevronsRight,
    CheckCircle2, XCircle, X, Fingerprint, AlertTriangle,
    ChevronUp, ChevronDown
} from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError } from "../../utils/toast";
import { useClientLevel } from "../../hooks/useClientLevel";
import { ItemsPerPageDropdown } from "../../components/ItemsPerPageDropdown ";

interface ADPasswordPolicy {
    id: number;
    created: string;
    updated: string;
    agent_id: number;
    client_id: number;
    asset_id: number;
    ad_domain_guid: string;
    domain: string;
    name: string;
    complexity_enabled: boolean;
    distinguished_name: string;
    lockout_duration: string;
    lockout_observation_window: string;
    lockout_threshold: string;
    object_guid: string;
    max_pwd_age: number;
    min_pwd_age: number;
    min_pwd_length: number;
    pwd_history_length: number;
    reversible_encryption_enabled: boolean;
}

const Val = ({ v }: { v: string | number | undefined | null }) => {
    const empty = v === undefined || v === null || v === "-" || v === "";
    return (
        <span className={empty ? " text-gray-200" : "text-gray-200"}>
            {empty ? "—" : String(v)}
        </span>
    );
};

const BoolBadge = ({
    value, trueLabel = "Yes", falseLabel = "No",
    trueColor = "text-green-400", falseColor = " text-gray-200"
}: {
    value: boolean;
    trueLabel?: string;
    falseLabel?: string;
    trueColor?: string;
    falseColor?: string;
}) => (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${value ? trueColor : falseColor}`}>
        {value ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
        {value ? trueLabel : falseLabel}
    </span>
);

// Lockout threshold = "0" means no lockout
const isNoLockout = (threshold: string) => threshold === "0" || threshold === "";

// ─── Detail Panel ─────────────────────────────────────────────────────────────
function PolicyDetailPanel({ policy, onClose }: { policy: ADPasswordPolicy; onClose: () => void }) {
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const Section = ({ title, icon, colorClass = "text-rose-400" }: { title: string; icon: React.ReactNode; colorClass?: string }) => (
        <div className="flex items-center gap-2 mb-3 mt-5 first:mt-0">
            <span className={colorClass}>{icon}</span>
            <span className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">{title}</span>
            <div className="flex-1 h-px bg-gray-700/50" />
        </div>
    );

    const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
        <div className="flex flex-col gap-0.5">
            <span className="text-[9px] text-gray-500 uppercase tracking-widest">{label}</span>
            <div className="text-[11px]">{children}</div>
        </div>
    );

    // Stat card for numeric policy values
    const StatCard = ({
        label, value, unit, icon, color
    }: {
        label: string;
        value: string | number;
        unit?: string;
        icon: React.ReactNode;
        color: string;
    }) => (
        <div className={`flex flex-col gap-1 p-3 rounded-lg border bg-gray-800/50 ${color}`}>
            <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest opacity-70">
                {icon} {label}
            </div>
            <div className="flex items-end gap-1">
                <span className="text-xl font-bold">{value}</span>
                {unit && <span className="text-[10px] opacity-60 mb-0.5">{unit}</span>}
            </div>
        </div>
    );

    return (
        <>
            <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-[1px]" onClick={onClose} />

            <div className="fixed top-0 right-0 h-full w-[30%] min-w-[340px] bg-gray-900 border-l border-gray-700 z-55 flex flex-col shadow-2xl animate-slide-in-right">

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/60 bg-gray-800/60 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-rose-600/20 border border-rose-500/30 flex items-center justify-center">
                            <KeyRound size={16} className="text-rose-400" />
                        </div>
                        <div>
                            <p className="text-[13px] font-bold text-white">{policy.name}</p>
                            <p className="text-[10px] font-mono text-gray-200">{policy.domain}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-gray-200 hover:text-white hover:bg-gray-700 transition-all">
                        <X size={15} />
                    </button>
                </div>

                {/* Pills */}
                <div className="flex flex-wrap gap-1.5 px-5 py-3 border-b border-gray-700/40 bg-gray-800/30 shrink-0">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold border ${policy.complexity_enabled
                        ? "bg-green-500/10 border-green-500/20 text-green-400"
                        : "bg-red-500/10 border-red-500/20 text-red-400"
                        }`}>
                        <Shield size={9} /> Complexity {policy.complexity_enabled ? "On" : "Off"}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold border ${isNoLockout(policy.lockout_threshold)
                        ? "bg-yellow-500/10 border-yellow-500/20 text-yellow-400"
                        : "bg-red-500/10 border-red-500/20 text-red-400"
                        }`}>
                        <Lock size={9} /> {isNoLockout(policy.lockout_threshold) ? "No Lockout" : `Lockout after ${policy.lockout_threshold}`}
                    </span>
                    {policy.reversible_encryption_enabled && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-red-500/10 border border-red-500/20 text-red-400">
                            <AlertTriangle size={9} /> Reversible Encryption
                        </span>
                    )}
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 text-gray-200">

                    {/* Password Rules — stat cards */}
                    <Section title="Password Rules" icon={<KeyRound size={12} />} colorClass="text-amber-400" />
                    <div className="grid grid-cols-2 gap-2 mb-1">
                        <StatCard
                            label="Min Length"
                            value={policy.min_pwd_length}
                            unit="chars"
                            icon={<Hash size={10} />}
                            color="border-rose-500/20 text-rose-300"
                        />
                        <StatCard
                            label="History"
                            value={policy.pwd_history_length}
                            unit="prev"
                            icon={<Clock size={10} />}
                            color="border-purple-500/20 text-purple-300"
                        />
                        <StatCard
                            label="Max Age"
                            value={policy.max_pwd_age}
                            unit="days"
                            icon={<Clock size={10} />}
                            color="border-orange-500/20 text-orange-300"
                        />
                        <StatCard
                            label="Min Age"
                            value={policy.min_pwd_age}
                            unit="days"
                            icon={<Clock size={10} />}
                            color="border-yellow-500/20 text-yellow-300"
                        />
                    </div>

                    {/* Security Settings */}
                    <Section title="Security Settings" icon={<Shield size={12} />} colorClass="text-blue-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Complexity">
                            <BoolBadge value={policy.complexity_enabled} trueColor="text-green-400" falseColor="text-red-400" />
                        </Field>
                        <Field label="Reversible Encryption">
                            <BoolBadge value={policy.reversible_encryption_enabled} trueColor="text-red-400" falseColor=" text-gray-200" />
                        </Field>
                    </div>

                    {/* Lockout Settings */}
                    <Section title="Lockout Settings" icon={<Lock size={12} />} colorClass="text-rose-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Threshold">
                            {isNoLockout(policy.lockout_threshold) ? (
                                <span className="text-yellow-400 font-bold text-[10px]">Disabled (0)</span>
                            ) : (
                                <span className="font-bold text-red-300">{policy.lockout_threshold} attempts</span>
                            )}
                        </Field>
                        <Field label="Duration">
                            <span className="font-mono text-[10px] text-gray-300"><Val v={policy.lockout_duration} /></span>
                        </Field>
                        <Field label="Observation Window">
                            <span className="font-mono text-[10px] text-gray-300"><Val v={policy.lockout_observation_window} /></span>
                        </Field>
                    </div>

                    {/* Identity */}
                    <Section title="Identity" icon={<Globe size={12} />} colorClass="text-cyan-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Policy Name"><Val v={policy.name} /></Field>
                        <Field label="Domain"><Val v={policy.domain} /></Field>
                    </div>

                    {/* Identifiers */}
                    <Section title="Identifiers" icon={<Fingerprint size={12} />} colorClass="text-purple-400" />
                    <div className="flex flex-col gap-3">
                        <Field label="Object GUID">
                            <span className="font-mono text-[10px] text-rose-300/80 break-all"><Val v={policy.object_guid} /></span>
                        </Field>
                        <Field label="Distinguished Name">
                            <span className="font-mono text-[10px] text-gray-200 break-all"><Val v={policy.distinguished_name} /></span>
                        </Field>
                    </div>

                    <div className="h-6" />
                </div>
            </div>
        </>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ADPasswordPolicies({ asset_id }: { asset_id: any }) {
    const [policies, setPolicies] = useState<ADPasswordPolicy[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selectedPolicy, setSelectedPolicy] = useState<ADPasswordPolicy | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    const level = useClientLevel();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchPolicies = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;

            if (debouncedSearch) {
                queryObj.$or = [
                    { name: { $ilike: `%${debouncedSearch}%` } },
                    { domain: { $ilike: `%${debouncedSearch}%` } },
                    { distinguished_name: { $ilike: `%${debouncedSearch}%` } },
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit
            };

            // Add API Sorting if requested
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/ad_password_policies", { params });

            if (response.data.status) {
                setPolicies(response.data.data);
                setTotalRecords(response.data.total ?? response.data.data.length);
            } else {
                showError("Failed to load password policies");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    }, [level, debouncedSearch, currentPage, limit, sortConfig, asset_id]);

    useEffect(() => {
        fetchPolicies();
    }, [fetchPolicies]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setCurrentPage(1); // Reset to page 1 on sort
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, icon }: { label: string; sortKey: string; icon?: React.ReactNode }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group select-none"
            >
                <div className="flex items-center gap-2">
                    {icon}
                    {label}
                    <div className="flex items-center ml-auto">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-rose-400" />
                            ) : (
                                <ChevronDown size={12} className="text-rose-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    return (
        <div className="flex flex-col bg-transparent text-gray-200">

            {selectedPolicy && (
                <PolicyDetailPanel policy={selectedPolicy} onClose={() => setSelectedPolicy(null)} />
            )}

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-rose-600/20 p-1.5 rounded-lg border border-rose-500/30">
                        <KeyRound size={16} className="text-rose-400" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">Password Policies</h1>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200 group-focus-within:text-rose-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search policies..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-rose-500/50 transition-all"
                        />
                    </div>
                    <button
                        onClick={fetchPolicies}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0 disabled:opacity-50"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-rose-400"} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <SortableHeader label="Policy" sortKey="name" icon={<KeyRound size={13} className="text-rose-400" />} />
                                <SortableHeader label="Domain" sortKey="domain" icon={<Globe size={13} className="text-indigo-400" />} />
                                <SortableHeader label="Min Length" sortKey="min_pwd_length" icon={<Hash size={13} className="text-cyan-400" />} />
                                <SortableHeader label="Max Age" sortKey="max_pwd_age" icon={<Clock size={13} className="text-orange-400" />} />
                                <SortableHeader label="Min Age" sortKey="min_pwd_age" icon={<Clock size={13} className="text-yellow-400" />} />
                                <SortableHeader label="History" sortKey="pwd_history_length" icon={<Hash size={13} className="text-purple-400" />} />
                                <SortableHeader label="Complexity" sortKey="complexity_enabled" icon={<Shield size={13} className="text-green-400" />} />
                                <SortableHeader label="Lockout" sortKey="lockout_threshold" icon={<Lock size={13} className="text-red-400" />} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-500">
                                            <Loader2 className="w-4 h-4 animate-spin text-rose-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading policies...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : policies.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <KeyRound size={24} className="text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">
                                                {debouncedSearch ? `No results for "${debouncedSearch}"` : "No password policies found"}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                policies.map((policy) => (
                                    <tr
                                        key={policy.id}
                                        onClick={() => setSelectedPolicy(policy)}
                                        className={`hover:bg-gray-800/60 transition-colors cursor-pointer ${selectedPolicy?.id === policy.id
                                            ? "bg-rose-500/5 border-l-2 border-l-rose-500"
                                            : ""
                                            }`}
                                    >
                                        {/* Policy Name */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full bg-rose-600/20 border border-rose-500/30 flex items-center justify-center shrink-0">
                                                    <KeyRound size={11} className="text-rose-400" />
                                                </div>
                                                <div>
                                                    <p className="text-[11px] font-bold text-gray-100">{policy.name}</p>
                                                    <p className="text-[9px] font-mono text-gray-500 truncate max-w-[180px]" title={policy.distinguished_name}>
                                                        {policy.distinguished_name}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Domain */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[11px] text-gray-300">{policy.domain || "—"}</span>
                                        </td>

                                        {/* Min Length */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-500/10 border border-cyan-500/20 rounded text-[10px] font-bold text-cyan-300">
                                                <Hash size={9} /> {policy.min_pwd_length} chars
                                            </span>
                                        </td>

                                        {/* Max Age */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-500/10 border border-orange-500/20 rounded text-[10px] font-bold text-orange-300">
                                                <Clock size={9} /> {policy.max_pwd_age}d
                                            </span>
                                        </td>

                                        {/* Min Age */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500/10 border border-yellow-500/20 rounded text-[10px] font-bold text-yellow-300">
                                                <Clock size={9} /> {policy.min_pwd_age}d
                                            </span>
                                        </td>

                                        {/* History */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-500/10 border border-purple-500/20 rounded text-[10px] font-bold text-purple-300">
                                                <Hash size={9} /> {policy.pwd_history_length} prev
                                            </span>
                                        </td>

                                        {/* Complexity */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <BoolBadge
                                                value={policy.complexity_enabled}
                                                trueLabel="Enabled"
                                                falseLabel="Disabled"
                                                trueColor="text-green-400"
                                                falseColor="text-red-400"
                                            />
                                        </td>

                                        {/* Lockout */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {isNoLockout(policy.lockout_threshold) ? (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-yellow-500/80">
                                                    <XCircle size={11} /> Disabled
                                                </span>
                                            ) : (
                                                <div className="flex flex-col gap-0.5">
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-400">
                                                        <Lock size={10} /> After {policy.lockout_threshold} attempts
                                                    </span>
                                                    <span className="text-[9px] font-mono text-gray-500">
                                                        dur: {policy.lockout_duration}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1  text-gray-200">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="text-[9px] text-rose-400 hover:text-rose-300 font-bold uppercase tracking-widest transition-colors"
                            >
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}